import React, { type FC, type PropsWithChildren } from 'react';

import { mqMin } from '@lucid/theme';
import reactHTMLParser from 'html-react-parser';

import Box from '../box/box';
import Container from '../container/container';
import Heading from '../heading/heading';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import Text from '../text/text';
import VideoPlayer from '../video/video-player/video-player';
import type { ComponentCTA, ComponentVideo } from '../../types/generics';
import type { Lucid } from '../../types/types';
import Link from '../link/link';

export interface SimpleVideoProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** CTA links to display beneath the SimpleVideo */
  ctas?: ComponentCTA[];
  /** Determines whether light text is needed for darker backgrounds */
  hasDarkBackground?: boolean;
  /** The heading for the SimpleVideo component. */
  heading?: string;
  /** Sets the variant for the SimpleVideo heading */
  headingVariant?: Lucid.Theme.Component.Heading['Variant'];
  /** The supporting text for the SimpleVideo component. */
  text?: string;
  /** Sets the text variant for the supporting text */
  textVariant?: Lucid.Theme.Component.Typography;
  /** The video for the SimpleVideo component */
  video: ComponentVideo;
  /** HTML block to be displayed with Simple Video component */
  videoContent?: string;
}

const SimpleVideo: FC<PropsWithChildren<SimpleVideoProps>> = ({
  className,
  ctas,
  hasDarkBackground,
  heading,
  headingVariant,
  text,
  textVariant = 'paragraphM400',
  video,
  videoContent,
  ...rest
}) => (
  <Container
    css={theme => ({
      paddingTop: theme.space.s40,
      paddingBottom: theme.space.s40,
    })}
    // eslint-disable-next-line react/jsx-sort-props
    className={className}
    {...rest}
  >
    <Box
      css={[
        {
          maxWidth: '780px',
          margin: '0 auto',
        },
        hasDarkBackground && { color: 'white' },
      ]}
    >
      {heading && (
        <Heading
          as="h2"
          css={theme => [
            { textAlign: 'center', margin: '0', color: 'inherit' },
            headingVariant && {
              // design calls for default mobile font size to be smaller than tablet & desktop
              fontSize: theme.fontSizes.L,
              [mqMin.tabletSmall]: { fontSize: theme.fontSizes.XL },
            },
          ]}
          variant={headingVariant ?? 'heading2XL'}
        >
          {heading}
        </Heading>
      )}
      {text && (
        <Text
          css={theme => ({ textAlign: 'center', marginTop: theme.space.s24, color: 'inherit' })}
          variant={textVariant}
        >
          {text}
        </Text>
      )}
    </Box>
    <Box
      css={theme => [
        {
          display: 'flex',
          flexDirection: 'column',
          margin: `${theme.space.s40}px auto 0`,
          maxWidth: '780px',
        },
        videoContent && {
          gap: theme.space.s40,
          maxWidth: '100%',
          [mqMin.tabletLandscape]: {
            flexDirection: 'row',
            gap: theme.space.s80,
          },
        },
      ]}
    >
      <Box css={videoContent && { [mqMin.tabletLandscape]: { flex: '0 1 680px' } }}>
        <VideoPlayer
          controls
          description={video.videoDescription}
          descriptionElementId={video.videoDescriptionElementId}
          placeholder={
            video.videoLinkPlaceholder
              ? () => (
                  <ImageWithPlaceholder alt={video.videoLinkPlaceholderAlt || ''} src={video.videoLinkPlaceholder!} />
                )
              : undefined
          }
          playing={false}
          url={video.videoLink}
        />
      </Box>
      <Box css={videoContent && { [mqMin.tabletLandscape]: { flex: '0 1 37%' } }}>
        {videoContent && (
          <Box
            css={theme => [
              theme.shared.htmlParserStyles,
              hasDarkBackground && {
                color: 'white',
              },
            ]}
          >
            {reactHTMLParser(videoContent)}
          </Box>
        )}
        {ctas && (
          <Box
            css={theme => [
              {
                paddingTop: theme.space.s24,
                display: 'flex',
                gap: theme.space.s40,
                alignItems: 'center',
                justifyContent: 'center',
              },
              videoContent && {
                justifyContent: 'flex-start',
              },
            ]}
          >
            {ctas
              ?.filter(({ url }) => !!url)
              .map((cta, idx) => {
                const { url, text } = cta;
                return (
                  <Link
                    key={`fieldCTA-${idx}`}
                    displayAsButton={!!cta.displayAsButton}
                    iconAnimation="navigational"
                    to={url}
                    variant={hasDarkBackground ? (cta.displayAsButton ? 'secondary' : 'primaryInverse') : 'primary'}
                  >
                    {text}
                  </Link>
                );
              })}
          </Box>
        )}
      </Box>
    </Box>
  </Container>
);

export { SimpleVideo };
export default SimpleVideo;
